// src/components/Sales/Sales.jsx
import React, { useState, useEffect } from 'react';
import salesService from '../../services/salesService';
import SalesTable from './SalesTable';
import CreateSaleModal from './CreateSaleModal';
import EditSaleModal from './EditSaleModal';
import InvoiceDetailsModal from './InvoiceDetailsModal';
import { showNotification } from '../../utils/notification';
import './Sales.css';
import { PlusCircle } from 'lucide-react';

const Sales = ({ toggleSidebar }) => {
  
  // ========== STATE MANAGEMENT ==========
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInvoiceDetailsOpen, setIsInvoiceDetailsOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [filters, setFilters] = useState({
    warehouse: '',
    salesPerson: '',
    category: '',
    startDate: '',
    endDate: '',
    searchText: ''
  });

  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ========== LOAD DATA ON MOUNT ==========
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [sales, filters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // ========== DATA LOADING ==========
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load ALL sales invoice data for client-side filtering
      const [salesData, invs, cats, emps, custs] = await Promise.all([
        salesService.getAllSales(), // Fetch all sales invoices at once
        salesService.getActiveInventories(),
        salesService.getActiveCategories(),
        salesService.getEmployees(),
        salesService.getCustomers()
      ]);
      
      setSales(salesData);
      setInventories(invs || []);
      setCategories(cats || []);
      setEmployees(emps || []);
      setCustomers(custs || []);
      
      console.log('Total sales loaded:', salesData.length);
      showNotification(`Loaded ${salesData.length} sales invoices`, 'success');
    } catch (error) {
      console.error('Error loading sales:', error);
      showNotification(`Failed to load: ${error.message}`, 'error');
      setSales([]);
      setInventories([]);
      setCategories([]);
      setEmployees([]);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========
  const applyFilters = () => {
    const safeSales = Array.isArray(sales) ? sales : [];
    let filtered = [...safeSales];

    // Filter by warehouse (inventory)
    if (filters.warehouse) {
      filtered = filtered.filter(s => {
        const inventoryId = s.inventory?.id || s.inventoryId;
        return inventoryId == filters.warehouse;
      });
    }

    // Filter by sales person
    if (filters.salesPerson) {
      filtered = filtered.filter(s => {
        const salesPersonId = s.salePerson?.id || s.salePersonId;
        return salesPersonId == filters.salesPerson;
      });
    }

    // Filter by category - check items in sale array
    if (filters.category) {
      filtered = filtered.filter(s => {
        if (!s.sale || !Array.isArray(s.sale)) return false;
        return s.sale.some(item => {
          const categoryId = item.product?.categoryId;
          return categoryId == filters.category;
        });
      });
    }

    // Filter by date range
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(s => {
        const saleDateStr = s.createdAt;
        if (!saleDateStr) return true;
        
        const saleDate = new Date(saleDateStr);
        saleDate.setHours(0, 0, 0, 0);
        return saleDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(s => {
        const saleDateStr = s.createdAt;
        if (!saleDateStr) return true;
        
        const saleDate = new Date(saleDateStr);
        return saleDate <= endDate;
      });
    }

    // Filter by search text
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase().trim();
      filtered = filtered.filter(s => {
        const invoiceMatch = (s.invoiceNumber || '').toLowerCase().includes(searchLower);
        const shopNameMatch = (s.customer?.shopName || s.customer?.name || '').toLowerCase().includes(searchLower);
        const inventoryMatch = (s.inventory?.name || '').toLowerCase().includes(searchLower);
        const salesPersonMatch = (s.salePerson?.firstName || s.salePerson?.lastName || '').toLowerCase().includes(searchLower);
        
        // Search in sale items
        const productMatch = s.sale && Array.isArray(s.sale) && s.sale.some(item => 
          (item.product?.name || '').toLowerCase().includes(searchLower)
        );
        
        return invoiceMatch || shopNameMatch || inventoryMatch || salesPersonMatch || productMatch;
      });
    }

    setFilteredSales(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ 
      warehouse: '', 
      salesPerson: '', 
      category: '',
      startDate: '',
      endDate: '',
      searchText: ''
    });
    showNotification('Filters cleared', 'info');
  };

  // Quick date filter shortcuts
  const setDateRange = (range) => {
    const today = new Date();
    let startDate = new Date();
    
    switch(range) {
      case 'today':
        startDate = new Date();
        break;
      case 'week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // ========== CLIENT-SIDE PAGINATION ==========
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSales = filteredSales.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) pages.push(i);
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
      }
    }
    
    return pages;
  };

  // ========== MODAL OPERATIONS ==========
  const handleAddNew = () => {
    setSelectedSale(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (sale) => {
    setSelectedSale(sale);
    setIsEditModalOpen(true);
  };

  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setIsInvoiceDetailsOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsInvoiceDetailsOpen(false);
    setSelectedSale(null);
  };

  // ========== CRUD OPERATIONS ==========
  const handleSave = async (payload) => {
    try {
      setIsSaving(true);
      
      const userData = JSON.parse(localStorage.getItem('user'));
      const salesPersonId = userData?.id;
      
      const apiPayload = {
        sales: payload.sales.map(sale => ({
          ...sale,
          salesPersonId: salesPersonId
        }))
      };
      
      await salesService.create(apiPayload);
      showNotification('Sale created successfully!', 'success');
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving sale:', error);
      showNotification(`Failed to save: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async (payload) => {
    try {
      setIsSaving(true);
      
      const userData = JSON.parse(localStorage.getItem('user'));
      const salesPersonId = userData?.id;
      
      const completePayload = {
        ...payload,
        salesPersonId: salesPersonId
      };
      
      await salesService.update(selectedSale.id, completePayload);
      showNotification('Sale updated successfully!', 'success');
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error updating sale:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (sale) => {
    const confirmDelete = window.confirm(`Delete sale #${sale.id}?`);
    if (!confirmDelete) return;

    try {
      await salesService.delete(sale.id);
      showNotification('Sale deleted successfully', 'success');
      await loadData();
    } catch (error) {
      console.error('Error deleting sale:', error);
      showNotification(`Failed to delete: ${error.message}`, 'error');
    }
  };

  // ========== RENDER ==========
  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn">☰</button>
        <h2 className="inventory-content-title">Sales Management</h2>
        <div className="header-actions">
          <button 
            className="btn btn-blue" 
            style={{ backgroundColor: "#1E3A8A", color: "white" }}
            onClick={handleAddNew}
          >
            <PlusCircle size={18} />
            Create New Sale
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="filter-controls">
        {/* Search Bar */}
        <div className="filter-group" style={{ marginBottom: '12px' }}>
          <input
            type="text"
            name="searchText"
            className="custom-input"
            placeholder="Search by invoice number, shop name, or product..."
            value={filters.searchText}
            onChange={handleFilterChange}
            style={{ flex: 1, minWidth: '300px' }}
          />
        </div>

        {/* Date Range with Quick Filters */}
        <div className="filter-group" style={{ marginBottom: '12px' }}>
          <label style={{ marginRight: '8px', fontWeight: '500' }}>Date Range:</label>
          <input
            type="date"
            name="startDate"
            className="custom-input"
            value={filters.startDate}
            onChange={handleFilterChange}
            style={{ width: '150px' }}
          />
          <span style={{ margin: '0 8px' }}>to</span>
          <input
            type="date"
            name="endDate"
            className="custom-input"
            value={filters.endDate}
            onChange={handleFilterChange}
            style={{ width: '150px' }}
          />
          
          {/* Quick Date Shortcuts */}
          <div style={{ marginLeft: '16px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-sm" onClick={() => setDateRange('today')}>Today</button>
            <button className="btn btn-sm" onClick={() => setDateRange('week')}>Last 7 Days</button>
            <button className="btn btn-sm" onClick={() => setDateRange('month')}>Last Month</button>
            <button className="btn btn-sm" onClick={() => setDateRange('year')}>Last Year</button>
          </div>
        </div>

        {/* Dropdown Filters */}
        <div className="filter-group">
          <select 
            name="warehouse"
            className="custom-select filter-select" 
            value={filters.warehouse} 
            onChange={handleFilterChange}
          >
            <option value="">All Warehouses</option>
            {inventories.map(inv => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
          </select>

          <select 
            name="salesPerson"
            className="custom-select filter-select" 
            value={filters.salesPerson} 
            onChange={handleFilterChange}
          >
            <option value="">All Sales Persons</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName}</option>)}
          </select>

          <select 
            name="category"
            className="custom-select filter-select" 
            value={filters.category} 
            onChange={handleFilterChange}
          >
            <option value="">All Categories</option>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
          </select>

          <button className="btn btn-clear" onClick={handleClearFilters}>
            Clear All Filters
          </button>
        </div>

        {/* Filter Summary with Items Per Page */}
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'left' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Showing {filteredSales.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredSales.length)} of <strong>{filteredSales.length}</strong> filtered sales (Total: <strong>{sales.length}</strong>)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: '#666' }}>Items per page:</label>
            <select 
              className="custom-select" 
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{ width: '80px' }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>
      </section>

      {/* Data Table */}
      <SalesTable 
        sales={currentSales}
        onViewDetails={handleViewDetails}
        isLoading={isLoading}
      />

      {/* Enhanced Pagination */}
      {filteredSales.length > 0 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '8px',
          marginTop: '24px',
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <button 
            className="btn" 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(currentPage - 1)}
            title="Previous Page"
            style={{ minWidth: '80px' }}
          >
            ‹ Prev
          </button>
          
          {/* Page Numbers */}
          <div style={{ display: 'flex', gap: '4px', margin: '0 12px' }}>
            {getPageNumbers().map((pageNum) => (
              <button
                key={pageNum}
                className={`btn ${currentPage === pageNum ? 'btn-page' : ''}`}
                onClick={() => handlePageChange(pageNum)}
                style={{ 
                  minWidth: '40px',
                  fontWeight: currentPage === pageNum ? 'bold' : 'normal'
                }}
              >
                {pageNum}
              </button>
            ))}
          </div>
          
          {/* Page Info */}
          <div style={{ 
            marginLeft: '16px', 
            padding: '8px 16px',
            backgroundColor: 'white',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            fontSize: '14px',
            color: '#495057',
            fontWeight: '500'
          }}>
            Page {currentPage} of {totalPages}
          </div>
          
          <button 
            className="btn btn-page" 
            disabled={currentPage === totalPages} 
            onClick={() => handlePageChange(currentPage + 1)}
            title="Next Page"
            style={{ minWidth: '80px' }}
          >
            Next ›
          </button>
        </div>
      )}

      {/* No Results Message */}
      {filteredSales.length === 0 && !isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6c757d',
          fontSize: '16px'
        }}>
          No sales found matching your filters. Try adjusting your search criteria.
        </div>
      )}

      {/* Create Modal */}
      <CreateSaleModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        isSaving={isSaving}
        inventories={inventories}
        categories={categories}
        employees={employees}
        customers={customers}
      />

      {/* Edit Modal */}
      <EditSaleModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveEdit}
        sale={selectedSale}
        isSaving={isSaving}
        inventories={inventories}
        categories={categories}
        employees={employees}
        customers={customers}
      />

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        isOpen={isInvoiceDetailsOpen}
        onClose={handleCloseModal}
        invoice={selectedSale}
      />
    </main>
  );
};

export default Sales;